import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Result, Spin, Typography, theme } from '@/shared/antd-imports';
import AuthPageHeader from '@/components/AuthPageHeader';
import { BrandAttribution } from '@/components/layout/BrandAttribution';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { loginWithMagicLink } from '@/store/slices/authSlice';
import { readMagicToken } from '@/utils/magic-link';


type MagicState = 'checking' | 'invalid' | 'error';

/**
 * ANS-55: the page a sign-in link opens. It removes the token from the address bar first (so it is not kept in the
 * history), exchanges it once for the normal session, then goes to the dashboard. A used, expired or unknown link
 * shows one message and a way to ask for a new link.
 */
const MagicLinkPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { token: { colorBgLayout } } = theme.useToken();
  const [state, setState] = useState<MagicState>('checking');
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const token = readMagicToken(window.location.hash);
    window.history.replaceState(null, '', window.location.pathname);
    if (!token) {
      setState('invalid');
      return;
    }
    dispatch(loginWithMagicLink(token))
      .unwrap()
      .then(() => navigate('/dashboard', { replace: true }))
      .catch((error: unknown) => {
        const text = typeof error === 'string' ? error.toLowerCase() : '';
        setState(text.includes('invalid') || text.includes('expired') || text.includes('used') ? 'invalid' : 'error');
      });
  }, [dispatch, navigate]);

  return (
    <div
      style={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 16,
        background: colorBgLayout,
        padding: '24px 16px',
      }}
    >
      <Card style={{ width: 420, maxWidth: '100%' }} styles={{ body: { padding: 'clamp(16px, 6vw, 32px)' } }}>
        <AuthPageHeader
          description={
            state === 'checking'
              ? t('login.magic.pageTitle', { defaultValue: 'Signing you in' })
              : t('login.magic.pageTitleFailed', { defaultValue: 'Sign-in link' })
          }
        />
        {state === 'checking' ? (
          <div role="status" aria-live="polite" style={{ textAlign: 'center', padding: 24 }}>
            <Spin size="large" />
            <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0 }}>
              {t('login.magic.checking', { defaultValue: 'Checking your sign-in link…' })}
            </Typography.Paragraph>
          </div>
        ) : (
          <Result
            status={state === 'invalid' ? 'warning' : 'error'}
            title={
              state === 'invalid'
                ? t('login.magic.invalidTitle', { defaultValue: 'This link does not work any more' })
                : t('login.magic.errorTitle', { defaultValue: 'Something went wrong' })
            }
            subTitle={
              state === 'invalid'
                ? t('login.magic.invalid', {
                    defaultValue: 'Sign-in links work once and expire after 15 minutes. Ask for a new one.',
                  })
                : t('login.magic.error', { defaultValue: 'We could not sign you in. Please try again.' })
            }
            extra={[
              <Button key="new" type="primary" onClick={() => navigate('/auth/login?mode=link', { replace: true })}>
                {t('login.magic.requestNew', { defaultValue: 'Send me a new link' })}
              </Button>,
              <Button key="password" onClick={() => navigate('/auth/login', { replace: true })}>
                {t('login.magic.usePassword', { defaultValue: 'Sign in with a password instead' })}
              </Button>,
            ]}
          />
        )}
      </Card>
      <BrandAttribution />
    </div>
  );
};

export default MagicLinkPage;
